import type { Project, ProjectType } from '@/lib/mockData'
import { projects } from '@/lib/mockData'

export type ColonyFeedKind = 'buy' | 'sell' | 'contribute'

export interface ColonyFeedItem {
  id: string
  projectId: string
  projectName: string
  ticker: string
  type: ProjectType
  shareSlug?: string
  kind: ColonyFeedKind
  amountUsd: number
  wallet: string
  createdAt: string
}

function hashString(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i += 1)
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0
  return hash
}

function fakeWallet(seed: string): string {
  const hex = hashString(seed).toString(16).padStart(8, '0')
  return `0x${hex.slice(0, 4)}…${hex.slice(-4)}`
}

function minutesAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60 * 1000).toISOString()
}

function amountFor(project: Project, index: number): number {
  const base = project.type === 'presale'
    ? Math.max(25, project.minContribution ?? 50)
    : 12
  const spread = [1, 2.4, 0.6, 4.1, 1.8, 0.35, 3.2]
  return Math.round(base * spread[index % spread.length] * 100) / 100
}

function kindFor(project: Project, index: number): ColonyFeedKind {
  if (project.type === 'presale')
    return 'contribute'
  return index % 5 === 0 ? 'sell' : 'buy'
}

function buildFeed(source: Project[], limit: number): ColonyFeedItem[] {
  const liveOrFunded = source.filter(project => project.status === 'live' || project.status === 'success' || project.status === 'ended')
  const pool = liveOrFunded.length > 0 ? liveOrFunded : source
  const items: ColonyFeedItem[] = []

  pool.forEach((project, projectIndex) => {
    const count = project.status === 'live' ? 3 : 2
    for (let i = 0; i < count; i += 1) {
      const offset = (projectIndex * 7 + i * 11) % 86 + 1
      items.push({
        id: `${project.id}-${i}`,
        projectId: project.id,
        projectName: project.name,
        ticker: project.ticker,
        type: project.type,
        shareSlug: project.shareSlug,
        kind: kindFor(project, i + projectIndex),
        amountUsd: amountFor(project, i + projectIndex),
        wallet: fakeWallet(`${project.id}-${i}`),
        createdAt: minutesAgo(offset),
      })
    }
  })

  return items
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, limit)
}

export function getColonyFeed(limit = 10): ColonyFeedItem[] {
  return buildFeed(projects, limit)
}

export function getProjectFeed(projectId: string, limit = 6): ColonyFeedItem[] {
  const project = projects.find(item => item.id === projectId)
  if (!project)
    return []
  return buildFeed([project], limit)
}
